<!--
  EditorCard.vue

  Shared chrome for cards stacked inside the store-editor sections.
  Per the 2026-05-29 campaign-editor design handoff: a card has an
  optional eyebrow (small uppercase tag like "NEW" or "DISTRIBUTOR
  DEFAULT"), a required title, an optional description, and slotted
  body content.

  Adoption sweep is PR A3; this component ships standalone in A1.
-->
<template>
    <section class="editor-card" data-testid="editor-card">
        <header class="editor-card__header">
            <div
                v-if="eyebrow"
                class="editor-card__eyebrow"
                data-testid="editor-card-eyebrow"
            >
                {{ eyebrow }}
            </div>
            <h3 class="editor-card__title" data-testid="editor-card-title">
                {{ title }}
            </h3>
            <p
                v-if="description"
                class="editor-card__description"
                data-testid="editor-card-description"
            >
                {{ description }}
            </p>
        </header>
        <div class="editor-card__body">
            <slot />
        </div>
    </section>
</template>

<script>
export default {
    name: "EditorCard",
    props: {
        title: { type: String, required: true },
        description: { type: String, default: "" },
        eyebrow: { type: String, default: "" },
    },
};
</script>

<style scoped>
.editor-card {
    background: var(--tf-bg);
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md, 12px);
    padding: var(--tf-sp-4, 16px);
}
.editor-card + .editor-card {
    margin-top: var(--tf-sp-4, 16px);
}
.editor-card__header {
    margin-bottom: var(--tf-sp-3, 12px);
}
.editor-card__eyebrow {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--tf-primary);
    margin-bottom: var(--tf-sp-1, 4px);
}
.editor-card__title {
    font-family: "Poppins", sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-fg-1);
    margin: 0;
    line-height: 1.4;
}
.editor-card__description {
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    color: var(--tf-fg-4);
    margin: var(--tf-sp-1, 4px) 0 0 0;
    line-height: 1.5;
}
/* This is the inherited base for EVERY store-editor form field's VALUE:
   Vuetify's .v-field__input is `color: inherit` + `opacity:
   var(--v-high-emphasis-opacity)` (VField.css:132-141), so whatever this
   rule sets is what the user's typed data composites down from.

   Moved from --tf-fg-2 to --tf-fg-1 in the round-4 label pass. At
   --tf-fg-2 (#333333) the value rendered #4e4e4e == 8.32:1, which was
   DARKER-THAN only by a hair over the newly-darkened 9.59:1 field label
   -- i.e. the label would have outranked the data it names. --tf-fg-1
   (#1c1c1c) puts the value at 11.37:1 and restores a clear step.

   The store editor was dimming its own data relative to every other page
   in the app (/distributor/account/company inherits black, 16.10:1), so
   this is a legibility fix in its own right, not just headroom for the
   label. Blast radius is one element: `.editor-card__body` is defined and
   used only in this file. */
.editor-card__body {
    color: var(--tf-fg-1);
}
</style>
